import { t } from "@lingui/core/macro";
import { useCallback, useEffect, useRef, useState } from "react";

// Keep voice recording deterministic: record and upload a single format.
const VOICE_MIME_TYPE = "audio/webm;codecs=opus";
const VOICE_MIME_TYPE_FALLBACK = "audio/webm";

export const VOICE_FILE_NAME = "voice.webm";

const pickSupportedMimeType = (): string | null => {
  if (typeof MediaRecorder === "undefined") {
    return null;
  }
  if (MediaRecorder.isTypeSupported(VOICE_MIME_TYPE)) {
    return VOICE_MIME_TYPE;
  }
  if (MediaRecorder.isTypeSupported(VOICE_MIME_TYPE_FALLBACK)) {
    return VOICE_MIME_TYPE_FALLBACK;
  }
  return null;
};

type UseVoiceRecorderArgs = {
  disabled?: boolean;
  onAudio: (audio: Blob) => void;
  onError?: (message: string) => void;
  onRecordingChange?: (isRecording: boolean) => void;
};

export function useVoiceRecorder({ disabled = false, onAudio, onError, onRecordingChange }: UseVoiceRecorderArgs) {
  // Keep returned handlers stable even if callbacks/flags change on each render.
  const disabledRef = useRef(disabled);
  const onAudioRef = useRef(onAudio);
  const onErrorRef = useRef(onError);
  const onRecordingChangeRef = useRef(onRecordingChange);

  useEffect(() => {
    disabledRef.current = disabled;
  }, [disabled]);
  useEffect(() => {
    onAudioRef.current = onAudio;
  }, [onAudio]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  useEffect(() => {
    onRecordingChangeRef.current = onRecordingChange;
  }, [onRecordingChange]);

  const [isRecording, setIsRecording] = useState(false);
  const isRecordingRef = useRef(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const setRecording = useCallback((next: boolean) => {
    setIsRecording(next);
    isRecordingRef.current = next;
    onRecordingChangeRef.current?.(next);
  }, []);

  const cleanup = useCallback(() => {
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) {
        track.stop();
      }
    }
    streamRef.current = null;
    recorderRef.current = null;
    chunksRef.current = [];
  }, []);

  const abort = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder) {
      recorder.onstop = null;
      if (recorder.state !== "inactive") {
        recorder.stop();
      }
    }
    cleanup();
    setRecording(false);
  }, [cleanup, setRecording]);

  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder) {
      cleanup();
      setRecording(false);
      return;
    }
    if (recorder.state !== "inactive") {
      recorder.stop();
    } else {
      cleanup();
    }
    setRecording(false);
  }, [cleanup, setRecording]);

  const start = useCallback(async () => {
    if (disabledRef.current || isRecordingRef.current) {
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      onErrorRef.current?.(t`Voice recording is not supported in this browser.`);
      return;
    }

    const mimeType = pickSupportedMimeType();
    if (!mimeType) {
      onErrorRef.current?.(t`Voice recording is not supported in this browser.`);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      const recorder = new MediaRecorder(stream, { mimeType });
      recorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const type = recorder.mimeType || VOICE_MIME_TYPE_FALLBACK;
        const audio = new Blob(chunksRef.current, { type });
        cleanup();
        if (audio.size === 0) {
          onErrorRef.current?.(t`No audio captured. Please try again.`);
          return;
        }
        onAudioRef.current(audio);
      };

      recorder.start();
      setRecording(true);
    } catch {
      onErrorRef.current?.(t`Microphone access was blocked. Please allow access and try again.`);
      cleanup();
      setRecording(false);
    }
  }, [cleanup, setRecording]);

  return {
    isRecording,
    start,
    stop,
    abort,
  };
}
